<template>
  <div class="modal" @click="$emit('close')">
    <div class="modal-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="close" @click="$emit('close')">
            <i class="fa fa-times"></i>
          </button>
          <h4 class="modal-title">Charges and Refunds</h4>
        </div>

        <!-- inform the user the card auth is being fetched -->
        <div class="modal-body" v-if="loading">
          <p>
            <i>Loading card details for order #{{ order.number }}...</i>
          </p>
        </div>

        <!-- in the unlikely event no card auth was found on the order, inform the user -->
        <div class="modal-body" v-if="!loading && !cardAuthorization">
          <p v-if="loadingError">
            There was an error fetching card authorization data for order #{{
              order.number
            }}. Please try again later.
          </p>
          <p v-else>
            <b>Order #{{ order.number }} has no card authorization.</b> Charges
            and refunds cannot be applied to this order.
          </p>
        </div>

        <!-- card authorization was found; display the form and card action history -->
        <template v-if="!loading && cardAuthorization">
          <div class="modal-body">
            <table class="table table-striped">
              <tbody>
                <tr>
                  <th>Authorization</th>
                  <td>
                    <b>${{ asCurrency(cardAuthorization.amount) }}</b>
                    on
                    <span
                      v-text="cardAuthorization.created_at | date-format 'long'"
                    ></span>
                  </td>
                </tr>
                <tr v-if="hasCharge">
                  <th>Charge</th>
                  <td>
                    <b>${{ asCurrency(cardAuthorization.charge.amount) }}</b>
                    on
                    <span
                      v-text="cardAuthorization.charge.created_at | date-format 'long'"
                    ></span>
                  </td>
                </tr>
                <tr v-if="hasRefund">
                  <th>Refund</th>
                  <td>
                    <b>${{ asCurrency(cardAuthorization.refund.amount) }}</b>
                    on
                    <span
                      v-text="cardAuthorization.refund.created_at | date-format 'long'"
                    ></span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- if a charge or refund is missing from the authorization, show the form -->
            <div class="row" v-if="canIssueRefund || canChargeCard">
              <div class="col-xs-4">
                <div class="form-group">
                  <label>Amount</label>
                  <div class="input-group">
                    <div class="input-group-addon">
                      <i class="fa fa-dollar-sign fa-sm"></i>
                    </div>
                    <input
                      inputmode="decimal"
                      class="form-control"
                      type="text"
                      :value="asCurrency(amount)"
                      @blur="updateAmount"
                      :readonly="processing"
                    />
                  </div>
                </div>
              </div>
              <div class="col-xs-8">
                <div class="form-group">
                  <label>Notes</label>
                  <input
                    class="form-control"
                    type="text"
                    v-model="notes"
                    placeholder="Notes (optional)"
                    :readonly="processing"
                  />
                </div>
              </div>
            </div>

            <div class="alert alert-danger" v-text="error" v-if="error"></div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn"
              @click="$emit('close')"
              v-text="canIssueRefund || canChargeCard ? 'Cancel' : 'Close'"
            ></button>

            <button
              class="btn btn-primary"
              :disabled="processing"
              v-if="!hasCharge || !hasRefund"
              @click="processCardAction"
            >
              <span v-if="processing"
                ><i class="fa fa-circle-notch fa-spin"></i> Processing...</span
              >
              <span
                v-else
                v-text="hasCharge ? 'Issue Refund' : 'Charge Card'"
              ></span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { Api, asCents, asCurrency } from '../utilities';

export default {
  props: {
    order: {
      type: Object,
      required: true,
    },
  },

  attached() {
    this.refresh();
  },

  data() {
    return {
      cardAuthorization: null,
      loading: true,
      loadingError: false,
      processing: false,
      amount: null,
      error: null,
      notes: null,
    };
  },

  computed: {
    hasCharge() {
      return (
        this.cardAuthorization && this.cardAuthorization.charge !== undefined
      );
    },

    hasRefund() {
      return (
        this.cardAuthorization && this.cardAuthorization.refund !== undefined
      );
    },

    canIssueRefund() {
      return this.hasCharge && !this.hasRefund;
    },

    canChargeCard() {
      return !this.hasCharge;
    },
  },

  methods: {
    asCurrency,
    asCents,

    async refresh() {
      try {
        this.cardAuthorization = null;
        this.loading = true;
        this.loadingError = false;
        this.error = null;
        this.notes = null;
        this.amount = null;
        this.processing = false;

        const response = await Api.get(`orders/${this.order.id}`, {
          params: { include: 'cardAuthorization' },
        });

        this.cardAuthorization = response.data.cardAuthorization || null;
      } catch (error) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },

    updateAmount(event) {
      this.amount = asCents(event.currentTarget.value);
    },

    validate() {
      this.error = null;

      if (this.amount <= 0) {
        this.error = 'The amount must be greater than $0.00.';

        return false;
      }

      return true;
    },

    async processCardAction() {
      if (!this.validate()) {
        return false;
      }

      this.processing = true;

      const action = this.hasCharge ? 'refund' : 'charge';
      const data = {
        amount: this.amount,
        notes: this.notes,
      };

      try {
        await Api.post(`orders/${this.order.id}/${action}`, { data });
        await this.refresh();

        return true;
      } catch (error) {
        this.error = `The ${action} failed to process. Please refer to the Wakefern payment summary screen, or call the help desk.`;

        return false;
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// ChargeAndRefund.vue?55a9cf94